import { lazy } from 'react';

/**
 * Declarative registry replacing the 17-case switch of the legacy
 * `activeRoute()` function.
 *
 * Each entry associates a route pattern with:
 *  - `flagKey`   user-detail field that controls "seen" state (persisted via API)
 *  - `titleKey`  i18n key used for the sheet title (falls back to static string)
 *  - `Component` lazy-loaded tutorial body
 *
 * Phase 2 migration complete: all 12 tutorials from `onboarding-legacy/` are
 * registered and wired to their paths (see
 * `frontend/docs/onboarding-rewrite-plan.md`).
 *
 * @typedef {object} OnboardingRouteConfig
 * @property {string} flagKey
 * @property {string} titleKey
 * @property {React.LazyExoticComponent<React.ComponentType<object>>} Component
 */

const DashboardTutorial = lazy(() => import('./tutorials/DashboardTutorial'));
const FeedTutorial = lazy(() => import('./tutorials/FeedTutorial'));
const CourseTutorial = lazy(() => import('./tutorials/CourseTutorial'));
const MyTasksTutorial = lazy(() => import('./tutorials/MyTasksTutorial'));
const ProjectsTutorial = lazy(() => import('./tutorials/ProjectsTutorial'));
const AvaliationsTutorial = lazy(() => import('./tutorials/AvaliationsTutorial'));
const MockQuestionBookTutorial = lazy(() => import('./tutorials/MockQuestionBookTutorial'));
const QuestionsTutorial = lazy(() => import('./tutorials/QuestionsTutorial'));
const AuditoriumTutorial = lazy(() => import('./tutorials/AuditoriumTutorial'));
const StudyPlanTutorial = lazy(() => import('./tutorials/StudyPlanTutorial'));
const ProfileTutorial = lazy(() => import('./tutorials/ProfileTutorial'));
const JourneyTutorial = lazy(() => import('./tutorials/JourneyTutorial'));

const DASHBOARD_CONFIG = {
  flagKey: 'showTutorialDashboard',
  titleKey: 'onboarding.dashboardTitle',
  Component: DashboardTutorial
};

/** @type {Record<string, OnboardingRouteConfig>} */
export const ONBOARDING_ROUTES = {
  '/': DASHBOARD_CONFIG,
  '/dashboard': DASHBOARD_CONFIG,
  '/feed': {
    flagKey: 'showTutorialFeed',
    titleKey: 'onboarding.feedTitle',
    Component: FeedTutorial
  },
  '/curso': {
    flagKey: 'showTutorialCourses',
    titleKey: 'onboarding.coursesTitle',
    Component: CourseTutorial
  },
  '/lms': {
    flagKey: 'showTutorialClasses',
    titleKey: 'onboarding.coursesTitle',
    Component: CourseTutorial
  },
  '/minhas-tarefas': {
    flagKey: 'showTutorialTasks',
    titleKey: 'onboarding.myTasksTitle',
    Component: MyTasksTutorial
  },
  '/projetos': {
    flagKey: 'showTutorialProjects',
    titleKey: 'onboarding.projectsTitle',
    Component: ProjectsTutorial
  },
  '/avaliacoes': {
    flagKey: 'showrTutorialQuestionBooks', // typo preservado do legacy (backend usa esta chave)
    titleKey: 'onboarding.avaliationsTitle',
    Component: AvaliationsTutorial
  },
  '/avaliacoes/meus-resultados': {
    flagKey: 'showTutorialQuestionBooks',
    titleKey: 'onboarding.avaliationsTitle',
    Component: AvaliationsTutorial
  },
  '/simulados': {
    flagKey: 'showTutorialMockQuestionBooks',
    titleKey: 'onboarding.mockQuestionBookTitle',
    Component: MockQuestionBookTutorial
  },
  '/auditorio': {
    flagKey: 'showTutorialAuditoriums',
    titleKey: 'onboarding.auditoriumTitle',
    Component: AuditoriumTutorial
  },
  '/plano-de-estudos': {
    flagKey: 'showTutorialStudyPlans',
    titleKey: 'onboarding.studyPlanTitle',
    Component: StudyPlanTutorial
  },
  '/profile': {
    flagKey: 'showTutorialProfiles',
    titleKey: 'onboarding.profileTitle',
    Component: ProfileTutorial
  }
};

/**
 * Pattern matchers for dynamic routes (legacy used literal ":param" strings
 * as switch cases — functionally dead since the real pathname has concrete
 * values. We use regex here to actually match the real URLs.).
 *
 * Each entry is evaluated in order; first match wins.
 *
 * @type {ReadonlyArray<{ test: (pathname: string) => boolean, config: OnboardingRouteConfig }>}
 */
const DYNAMIC_ROUTE_MATCHERS = [
  {
    // /usuario/<selectedTab>/<id>  → Journey tutorial
    test: pathname => /^\/usuario\/[^/]+\/[^/]+$/.test(pathname),
    config: {
      flagKey: 'showTutorialJourney',
      titleKey: 'onboarding.journeyTitle',
      Component: JourneyTutorial
    }
  },
  {
    // /questions/<selectedTab>  → Questions tutorial
    test: pathname => /^\/questions\/[^/]+$/.test(pathname),
    config: {
      flagKey: 'showTutorialQuestions',
      titleKey: 'onboarding.questionsTitle',
      Component: QuestionsTutorial
    }
  }
];

/**
 * Returns the onboarding config for a given pathname, or `undefined` when
 * no tutorial exists for that route.
 *
 * @param {string} pathname
 * @returns {OnboardingRouteConfig | undefined}
 */
export function getOnboardingConfig(pathname) {
  if (!pathname) {
    return undefined;
  }

  const exactMatch = ONBOARDING_ROUTES[pathname];
  if (exactMatch) {
    return exactMatch;
  }

  const dynamicMatch = DYNAMIC_ROUTE_MATCHERS.find(entry => entry.test(pathname));
  return dynamicMatch?.config;
}
